<template>
    <user-current v-slot="{ data: user }">
        <div>
            <div class="mb-6 bg-white rounded shadow-lg">
                <users-subscription-invoke
                    v-slot="{
                        data: subscription,
                        meta,
                        has_loaded,
                        is_loading,
                        invoke,
                        error
                    }"
                    :params="{
                        user: user.uuid
                    }"
                >
                    <div v-if="!has_loaded || error">
                        <base-loading
                            :error="error"
                            :retry="invoke"
                            class="py-6"
                        />

                        <error-modal
                            v-if="error"
                            :error="error"
                        />
                    </div>

                    <div v-else>
                        <header class="flex items-center px-4 border-b h-18 sm:px-6">
                            <div class="flex-grow">
                                <h1 class="text-lg font-normal">Subscription</h1>
                            </div>

                            <div class="w-6 h-6 mr-3">
                                <base-svg
                                    v-show="has_loaded && is_loading"
                                    name="icon-loading"
                                    class="w-full h-full fill-current text-grey"
                                />
                            </div>
                        </header>

                        <template v-if="subscription && !subscription.has_ended">
                            <div class="p-6">
                                <div class="mb-3 md:flex md:items-center">
                                    <div class="flex-grow">
                                        <div class="text-xl font-bold">
                                            {{ $t(`subscriptions.plans.${subscription.name}`) }}
                                        </div>
                                    </div>

                                    <div>
                                        <span class="font-bold">{{ subscription.amount | money(subscription.currency) }}</span>
                                        / {{ $t(`subscriptions.non_countable_noun.${subscription.name}`) }}
                                    </div>
                                </div>

                                <div>
                                    Billed from {{ meta.bank_card.data.brand }} ending {{ meta.bank_card.data.last_four }}
                                </div>

                                <div
                                    v-if="subscription.is_cancelled"
                                    class="mt-6 text-red"
                                >
                                    Subscription cancelled. You can continue to use your account until the end of your current term on {{ subscription.ends_at | moment('DD/MM/YYYY') }}.
                                </div>
                            </div>

                            <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                                <template v-if="!subscription.is_cancelled">
                                    <div class="flex flex-col md:flex-row-reverse">
                                        <div class="flex flex-col flex-grow md:flex-row-reverse">
                                            <button
                                                v-if="false"
                                                class="w-full mb-2 md:mb-0 md:w-auto btn btn-blue"
                                            >
                                                Upgrade plan
                                            </button>

                                            <subscriptions-cancel-invoke-button
                                                :subscription="subscription"
                                                @success="invoke"
                                                class="w-full px-0 mb-2 md:mb-0 md:w-auto btn btn-red-transparent"
                                            >
                                                Cancel subscription
                                            </subscriptions-cancel-invoke-button>
                                        </div>

                                        <router-link
                                            :to="{
                                                name: 'dashboard.users.bank-cards.create'
                                            }"
                                            class="w-full px-0 md:w-auto btn btn-transparent"
                                        >
                                            <p>Update payment method</p>
                                        </router-link>
                                    </div>
                                </template>

                                <template v-else>
                                    <subscriptions-reactivate-invoke-button
                                        :subscription="subscription"
                                        @success="invoke"
                                        class="w-full mb-2 md:mb-0 md:w-auto btn btn-blue"
                                    >
                                        Reactivate subscription
                                    </subscriptions-reactivate-invoke-button>
                                </template>
                            </div>
                        </template>

                        <template v-else>
                            <div class="p-6">
                                <div class="mb-3 md:flex md:items-center">
                                    <div class="flex-grow">
                                        <div class="text-xl font-bold">
                                            Not Subscribed
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    You don't currently have an active plan with Rentora.
                                </div>
                            </div>

                            <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                                <router-link
                                    :to="{
                                        name: 'dashboard.users.subscriptions.create'
                                    }"
                                    class="btn btn-blue btn-sm"
                                >
                                    Subscribe
                                </router-link>
                            </div>
                        </template>
                    </div>
                </users-subscription-invoke>
            </div>

            <div class="mb-6 bg-white rounded shadow-lg">
                <payment-intents-get
                    v-slot="{
                        data: intents,
                        has_loaded,
                        is_loading,
                        error,
                        get,
                        meta
                    }"
                    :params="{
                        page: 1,
                        per_page: 5,
                        as_user: true
                    }"
                    :include="[
                        'charge'
                    ]"
                >
                    <div>
                        <header class="flex items-center px-4 border-b h-18 sm:px-6">
                            <div class="flex-grow">
                                <h1 class="text-lg font-normal">Invoices</h1>
                            </div>

                            <div class="w-6 h-6 mr-3">
                                <base-svg
                                    v-show="has_loaded && is_loading"
                                    name="icon-loading"
                                    class="w-full h-full fill-current text-grey"
                                />
                            </div>
                        </header>

                        <div v-if="!has_loaded || error">
                            <base-loading
                                :error="error"
                                :retry="get"
                                class="py-6"
                            />

                            <error-modal
                                v-if="error"
                                :error="error"
                            />
                        </div>

                        <div
                            v-else-if="intents.length == 0"
                            class="p-3 italic text-center text-grey bg-grey-bg"
                        >
                            There's nothing here!
                        </div>

                        <div v-else>
                            <table-ish>
                                <table-ish-row-router-link
                                    v-for="intent in intents"
                                    :key="intent.uuid"
                                    :to="{
                                        name: 'dashboard.users.payment-intents.show',
                                        params: {
                                            payment_intent: intent.uuid
                                        }
                                    }"
                                >
                                    <table-ish-cell class="pl-6">
                                        {{ intent.charge.created_at | moment('DD/MM/YYYY') }}

                                        <template slot="secondary">
                                            <span
                                                :class="paymentIntentStatusCss(intent.status)"
                                                class="px-2 text-xs font-semibold leading-none rounded-full"
                                            >
                                                {{ $t(`payment_intents.status.${intent.status}`) }}
                                            </span>
                                        </template>
                                    </table-ish-cell>

                                    <table-ish-cell class="pr-6 text-right">
                                        <span
                                            :class="{
                                                'line-through': intent.status === 'failed',
                                                'text-grey-dark': intent.status === 'failed'
                                            }"
                                        >
                                            {{ intent.charge.amount | money }}
                                        </span>
                                    </table-ish-cell>
                                </table-ish-row-router-link>
                            </table-ish>

                            <div class="px-4 py-2 text-sm font-semibold rounded-b bg-grey-bg text-grey-darker sm:px-6">
                                <base-pagination
                                    v-model="page"
                                    :per-page="meta.per_page"
                                    :from="meta.from"
                                    :to="meta.to"
                                    :last-page="meta.last_page"
                                    :count="meta.count"
                                    :total="meta.total"
                                    :is-disabled="is_loading"
                                />
                            </div>
                        </div>
                    </div>
                </payment-intents-get>
            </div>
        </div>
    </user-current>
</template>

<script>
    import StateCssMixin from '../../../../mixins/state-css'
    import PaginationMixin from '../../../../mixins/pagination'
    import SubscriptionsCancelInvokeButton from '../../../../components/subscriptions-cancel-invoke-button'
    import UsersSubscriptionInvoke from '../../../../components/users-subscription-invoke'
    import SubscriptionsReactivateInvokeButton from '../../../../components/subscriptions-reactivate-invoke-button'
    import PaymentIntentsGet from '../../../../components/payment-intents-get'
    import UserCurrent from '../../../../components/users-current'

    export default {
        mixins: [
            StateCssMixin,
            PaginationMixin
        ],
        components: {
            SubscriptionsCancelInvokeButton,
            UsersSubscriptionInvoke,
            SubscriptionsReactivateInvokeButton,
            PaymentIntentsGet,
            UserCurrent
        }
    }
</script>
